import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  Download as DownloadIcon,
  Print as PrintIcon,
  FilterList as FilterIcon,
  TrendingUp as TrendingUpIcon,
  Agriculture as AgricultureIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

interface Statistics {
  totalSurveys: number;
  totalFarmers: number;
  totalParticipants: number;
  totalCrops: number;
  surveysByMonth: Array<{ month: string; count: number }>;
  surveysByGovernorate: Array<{ name: string; count: number }>;
  surveysByCrop: Array<{ name: string; count: number }>;
  topFarmers: Array<{ name: string; surveys: number }>;
}

const COLORS = ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#F44336', '#00BCD4', '#FFEB3B', '#795548'];

const ReportsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [selectedGovernorate, setSelectedGovernorate] = useState('all');
  const [governorates, setGovernorates] = useState<Array<{ id: number; name_ar: string; name_en: string }>>([]);
  const [error, setError] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);

  useEffect(() => {
    fetchStatistics();
  }, [selectedPeriod, selectedGovernorate]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      
      // Fetch various statistics
      const [surveysRes, farmersRes, participantsRes, statsRes, governoratesRes] = await Promise.all([
        api.get('/seeds/surveys/'),
        api.get('/seeds/farmers/'),
        api.get('/seeds/people/'),
        api.get('/dashboard/statistics/'),
        api.get('/locations/governorates/'),
      ]);

      const surveys = surveysRes.data.results || surveysRes.data;
      const farmers = farmersRes.data.results || farmersRes.data;
      const participants = participantsRes.data.results || participantsRes.data;
      const stats = statsRes.data;
      const governoratesData = governoratesRes.data.results || governoratesRes.data;
      setGovernorates(governoratesData);

      // Process data for charts
      const surveysByMonth = processMonthlyData(surveys);
      // Use governorate_stats from API and map to expected format
      const surveysByGovernorate = (stats.governorate_stats || []).map((gov: any) => ({
        name_ar: gov.name_ar,
        name: gov.name_en,
        count: gov.survey_count
      }));
      // Use top_crops from API and map to expected format  
      const surveysByCrop = (stats.top_crops || []).map((crop: any) => ({
        name_ar: crop.name_ar,
        name: crop.name_en,
        count: crop.survey_count
      }));
      const topFarmers = processTopFarmers(farmers);

      setStatistics({
        totalSurveys: stats.total_surveys || surveys.length,
        totalFarmers: stats.total_farmers || farmers.length,
        totalParticipants: stats.total_participants || participants.length,
        totalCrops: stats.total_crops || 0,
        surveysByMonth,
        surveysByGovernorate,
        surveysByCrop,
        topFarmers,
      });
    } catch (error: any) {
      console.error('Error fetching statistics:', error);
      setError(error.response?.data?.message || t('errorFetchingData'));
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const processMonthlyData = (surveys: any[]) => {
    const monthCounts: { [key: string]: number } = {};
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    
    // Initialize all months with 0
    months.forEach(month => {
      monthCounts[month] = 0;
    });

    // Count surveys by month
    surveys.forEach(survey => {
      if (survey.date_collected) {
        const date = new Date(survey.date_collected);
        const monthIndex = date.getMonth();
        monthCounts[months[monthIndex]]++;
      }
    });

    return months.map(month => ({
      month: t(month.toLowerCase()),
      count: monthCounts[month],
    }));
  };

  const processTopFarmers = (farmers: any[]) => {
    return farmers
      .sort((a, b) => (b.surveys_count || 0) - (a.surveys_count || 0))
      .slice(0, 5)
      .map(farmer => ({
        name: farmer.name,
        surveys: farmer.surveys_count || 0,
      }));
  };

  const handleExport = async (format: 'pdf' | 'excel') => {
    try {
      const response = await api.get(`/reports/export/?format=${format}`, {
        responseType: 'blob' as 'json',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_${new Date().toISOString()}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error: any) {
      console.error('Error exporting report:', error);
      setError(error.response?.data?.message || t('errorExportingReport'));
      setOpenSnackbar(true);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            {t('reports')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('reportsPageDescription')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Tooltip title={t('exportPDF')}>
            <IconButton
              onClick={() => handleExport('pdf')}
              sx={{
                bgcolor: 'error.main',
                color: 'white',
                '&:hover': { bgcolor: 'error.dark' },
              }}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('exportExcel')}>
            <IconButton
              onClick={() => handleExport('excel')}
              sx={{
                bgcolor: 'success.main',
                color: 'white',
                '&:hover': { bgcolor: 'success.dark' },
              }}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('print')}>
            <IconButton
              onClick={() => window.print()}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            >
              <PrintIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FilterIcon color="action" />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t('period')}</InputLabel>
            <Select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              label={t('period')}
            >
              <MenuItem value="all">{t('allTime')}</MenuItem>
              <MenuItem value="month">{t('thisMonth')}</MenuItem>
              <MenuItem value="quarter">{t('thisQuarter')}</MenuItem>
              <MenuItem value="year">{t('thisYear')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t('governorate')}</InputLabel>
            <Select
              value={selectedGovernorate}
              onChange={(e) => setSelectedGovernorate(e.target.value)}
              label={t('governorate')}
            >
              <MenuItem value="all">{t('allGovernorates')}</MenuItem>
              {governorates.map((gov) => (
                <MenuItem key={gov.id} value={gov.id.toString()}>
                  {i18n.language === 'ar' ? gov.name_ar : gov.name_en}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">
                    {statistics?.totalSurveys || 0}
                  </Typography>
                  <Typography variant="body1">
                    {t('totalSurveys')}
                  </Typography>
                </Box>
                <AssignmentIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">
                    {statistics?.totalFarmers || 0}
                  </Typography>
                  <Typography variant="body1">
                    {t('totalFarmers')}
                  </Typography>
                </Box>
                <AgricultureIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">
                    {statistics?.totalParticipants || 0}
                  </Typography>
                  <Typography variant="body1">
                    {t('totalParticipants')}
                  </Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">
                    {statistics?.totalCrops || 0}
                  </Typography>
                  <Typography variant="body1">
                    {t('totalCrops')}
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3}>
        {/* Monthly Surveys Chart */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('monthlySurveys')}
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={statistics?.surveysByMonth || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#4CAF50" 
                  strokeWidth={2}
                  name={t('surveys')}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Top Farmers */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              {t('topFarmers')}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('rank')}</TableCell>
                    <TableCell>{t('name')}</TableCell>
                    <TableCell align="right">{t('surveys')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statistics?.topFarmers.map((farmer, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Chip 
                          label={index + 1} 
                          size="small" 
                          color={index === 0 ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{farmer.name}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {farmer.surveys}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Surveys by Governorate */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('surveysByGovernorate')}
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={statistics?.surveysByGovernorate || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey={i18n.language === 'ar' ? 'name_ar' : 'name'} />
                <YAxis />
                <RechartsTooltip />
                <Bar dataKey="count" fill="#2196F3" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Surveys by Crop */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('surveysByCrop')}
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statistics?.surveysByCrop || []}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => `${i18n.language === 'ar' ? entry.name_ar : entry.name}: ${entry.count}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {statistics?.surveysByCrop.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Snackbar for error messages */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReportsPage;
